import dayjs from "dayjs";
import { z } from "zod";
import type { CalSchedule, CalScheduleAvailability, CalScheduleOverride } from "@/lib/cal/client";

export const DEFAULT_TIMEZONE = "America/Los_Angeles";
const DEFAULT_AVAILABILITY_NAME = "Vibrant Availability";
export const DEFAULT_AVAILABILITY_START = "09:00";
export const DEFAULT_AVAILABILITY_END = "17:00";

// Cal has no "unavailable" flag on an override. It marks a blocked date with a
// zero-length range (startTime === endTime). We model that as a date override
// with no ranges (`ranges: []`) and translate both ways at the Cal boundary.
const BLOCKED_OVERRIDE_TIME = "00:00";

export const CAL_DAY_OF_WEEK_VALUES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

const DEFAULT_AVAILABILITY_DAYS: Array<(typeof CAL_DAY_OF_WEEK_VALUES)[number]> = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

export interface AvailabilityTimeRange {
  start: string;
  end: string;
}

export interface AvailabilityDateOverride {
  date: string;
  ranges: AvailabilityTimeRange[];
}

export interface AvailabilityFormValues {
  name: string;
  timeZone: string;
  schedule: AvailabilityTimeRange[][];
  dateOverrides: AvailabilityDateOverride[];
  isDefault: boolean;
  inheritHostAvailability?: boolean;
}

const AvailabilityRangeSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

const AvailabilityDateOverrideSchema = z.object({
  date: z.string().min(1),
  ranges: z.array(AvailabilityRangeSchema),
});

const AvailabilityScheduleSchema = z.object({
  days: z.array(z.enum(CAL_DAY_OF_WEEK_VALUES)).min(1),
  startTime: z.string().min(1),
  endTime: z.string().min(1),
});

const AvailabilityScheduleOverrideSchema = z.object({
  date: z.string().min(1),
  startTime: z.string().min(1),
  endTime: z.string().min(1),
});

export const AvailabilityScheduleInputSchema = z.object({
  name: z.string().min(1),
  timeZone: z.string().min(1),
  availability: z.array(AvailabilityScheduleSchema),
  isDefault: z.boolean(),
  overrides: z.array(AvailabilityScheduleOverrideSchema).optional(),
});

export const AvailabilityFormValuesSchema = z.object({
  name: z.string().min(1),
  timeZone: z.string().min(1),
  schedule: z.array(z.array(AvailabilityRangeSchema)),
  dateOverrides: z.array(AvailabilityDateOverrideSchema),
  isDefault: z.boolean(),
  inheritHostAvailability: z.boolean().optional(),
});

export type AvailabilityScheduleInput = z.infer<typeof AvailabilityScheduleInputSchema>;

function formatTimeRangeValue(value: unknown): string | null {
  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("HH:mm") : null;
  }

  if (typeof value === "string") {
    const isoTimeMatch = value.match(/T(\d{2}:\d{2})/);
    if (isoTimeMatch) {
      return isoTimeMatch[1];
    }
    const directMatch = value.match(/^\d{2}:\d{2}/);
    if (directMatch) {
      return directMatch[0];
    }
    const embeddedMatch = value.match(/(\d{2}:\d{2})/);
    if (embeddedMatch) {
      return embeddedMatch[1];
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("HH:mm") : null;
  }

  return null;
}

/**
 * Parses a HH:mm time string into total minutes.
 */
function parseTimeToMinutes(value: string): number | null {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number.parseInt(match[1] ?? "", 10);
  const minutes = Number.parseInt(match[2] ?? "", 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
}

/**
 * Returns true when the end time is after the start time.
 */
function isValidTimeRange({ startTime, endTime }: { startTime: string; endTime: string }): boolean {
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);
  if (startMinutes == null || endMinutes == null) {
    return false;
  }

  return endMinutes > startMinutes;
}

function toAvailabilityRanges(
  schedule: AvailabilityFormValues["schedule"],
): CalScheduleAvailability[] {
  if (!Array.isArray(schedule)) {
    return [];
  }

  return schedule.flatMap((ranges, dayIndex) => {
    if (!Array.isArray(ranges)) {
      return [];
    }

    const dayName = CAL_DAY_OF_WEEK_VALUES[dayIndex];
    if (!dayName) {
      return [];
    }

    return ranges.flatMap((range) => {
      const parsed = AvailabilityRangeSchema.safeParse(range);
      if (!parsed.success) {
        return [];
      }

      const startTime = formatTimeRangeValue(parsed.data.start);
      const endTime = formatTimeRangeValue(parsed.data.end);
      if (!startTime || !endTime || !isValidTimeRange({ startTime, endTime })) {
        return [];
      }

      return [{ days: [dayName], startTime, endTime }];
    });
  });
}

function compareTimeRangeStarts(left: AvailabilityTimeRange, right: AvailabilityTimeRange): number {
  const leftStart = parseTimeToMinutes(left.start) ?? -1;
  const rightStart = parseTimeToMinutes(right.start) ?? -1;
  if (leftStart !== rightStart) {
    return leftStart - rightStart;
  }

  const leftEnd = parseTimeToMinutes(left.end) ?? -1;
  const rightEnd = parseTimeToMinutes(right.end) ?? -1;
  return leftEnd - rightEnd;
}

function buildAvailabilityDateOverridesFromCal({
  overrides,
}: {
  overrides?: CalScheduleOverride[];
}): AvailabilityDateOverride[] {
  if (!Array.isArray(overrides)) {
    return [];
  }

  const rangesByDate = new Map<string, AvailabilityTimeRange[]>();
  // A date Cal sent only as a zero-length range — blocked, no bookable hours.
  const blockedDates = new Set<string>();
  overrides.forEach((override) => {
    if (!override?.date) {
      return;
    }

    const start = formatTimeRangeValue(override.startTime);
    const end = formatTimeRangeValue(override.endTime);
    if (start && end && isValidTimeRange({ startTime: start, endTime: end })) {
      const ranges = rangesByDate.get(override.date) ?? [];
      ranges.push({ start, end });
      rangesByDate.set(override.date, ranges);
      return;
    }

    // start === end is Cal's blocked-date marker. Remember it unless real hours
    // on the same date already win.
    if (start && end && start === end) {
      blockedDates.add(override.date);
    }
  });

  const allDates = new Set<string>([...rangesByDate.keys(), ...blockedDates]);
  return Array.from(allDates)
    .sort((leftDate, rightDate) => leftDate.localeCompare(rightDate))
    .map((date) => {
      const ranges = rangesByDate.get(date) ?? [];
      return {
        date,
        ranges: [...ranges].sort(compareTimeRangeStarts),
      };
    });
}

function toAvailabilityOverrides(
  dateOverrides: AvailabilityFormValues["dateOverrides"],
): CalScheduleOverride[] {
  if (!Array.isArray(dateOverrides)) {
    return [];
  }

  const overrides = dateOverrides.flatMap((dateOverride) => {
    if (!dateOverride?.date || !Array.isArray(dateOverride.ranges)) {
      return [];
    }

    // No ranges = blocked date. Emit Cal's zero-length marker so the block
    // persists; without it the date falls back to the weekly schedule.
    if (dateOverride.ranges.length === 0) {
      return [
        {
          date: dateOverride.date,
          startTime: BLOCKED_OVERRIDE_TIME,
          endTime: BLOCKED_OVERRIDE_TIME,
        },
      ];
    }

    return dateOverride.ranges.flatMap((range) => {
      const parsed = AvailabilityRangeSchema.safeParse(range);
      if (!parsed.success) {
        return [];
      }

      const startTime = formatTimeRangeValue(parsed.data.start);
      const endTime = formatTimeRangeValue(parsed.data.end);
      if (!startTime || !endTime || !isValidTimeRange({ startTime, endTime })) {
        return [];
      }

      return [
        {
          date: dateOverride.date,
          startTime,
          endTime,
        },
      ];
    });
  });

  return overrides.sort((left, right) => {
    const dateComparison = left.date.localeCompare(right.date);
    if (dateComparison !== 0) {
      return dateComparison;
    }

    const startComparison =
      (parseTimeToMinutes(left.startTime) ?? -1) - (parseTimeToMinutes(right.startTime) ?? -1);
    if (startComparison !== 0) {
      return startComparison;
    }

    return (parseTimeToMinutes(left.endTime) ?? -1) - (parseTimeToMinutes(right.endTime) ?? -1);
  });
}

/**
 * Builds an empty weekly schedule scaffold.
 */
function buildEmptyWeeklySchedule(): AvailabilityTimeRange[][] {
  return Array.from({ length: CAL_DAY_OF_WEEK_VALUES.length }, () => []);
}

/**
 * Builds the default weekly schedule (Mon-Fri, 9-5).
 */
export function buildDefaultWeeklySchedule(): AvailabilityTimeRange[][] {
  const schedule = buildEmptyWeeklySchedule();
  for (const [index, day] of CAL_DAY_OF_WEEK_VALUES.entries()) {
    if (!DEFAULT_AVAILABILITY_DAYS.includes(day)) {
      continue;
    }
    schedule[index] = [
      {
        start: DEFAULT_AVAILABILITY_START,
        end: DEFAULT_AVAILABILITY_END,
      },
    ];
  }
  return schedule;
}

/**
 * Builds the Cal schedule availability payload from form state.
 */
function buildDefaultAvailability(): CalScheduleAvailability[] {
  return [
    {
      days: [...DEFAULT_AVAILABILITY_DAYS],
      startTime: DEFAULT_AVAILABILITY_START,
      endTime: DEFAULT_AVAILABILITY_END,
    },
  ];
}

/**
 * Expands Cal schedule availability into day-indexed ranges.
 */
export function buildAvailabilityScheduleFromCal({
  availability,
}: {
  availability: CalScheduleAvailability[];
}): AvailabilityTimeRange[][] {
  const schedule = buildEmptyWeeklySchedule();
  for (const slot of availability) {
    const { startTime, endTime } = slot;
    if (!startTime || !endTime || !isValidTimeRange({ startTime, endTime })) {
      continue;
    }

    slot.days.forEach((day) => {
      const dayIndex = CAL_DAY_OF_WEEK_VALUES.indexOf(day);
      if (dayIndex === -1) {
        return;
      }

      schedule[dayIndex] = [
        ...schedule[dayIndex],
        {
          start: startTime,
          end: endTime,
        },
      ];
    });
  }
  return schedule;
}

/**
 * Builds availability form values from an optional Cal schedule.
 */
export function buildAvailabilityFormValues({
  schedule,
  timeZoneFallback,
  nameOverride,
  isDefault,
  inheritHostAvailability = false,
}: {
  schedule?: CalSchedule | null;
  timeZoneFallback?: string;
  nameOverride?: string;
  isDefault: boolean;
  inheritHostAvailability?: boolean;
}): AvailabilityFormValues {
  if (!schedule) {
    return {
      name: nameOverride?.trim() || DEFAULT_AVAILABILITY_NAME,
      timeZone: timeZoneFallback || DEFAULT_TIMEZONE,
      schedule: inheritHostAvailability ? buildEmptyWeeklySchedule() : buildDefaultWeeklySchedule(),
      dateOverrides: [],
      isDefault,
      inheritHostAvailability: inheritHostAvailability || undefined,
    };
  }

  return {
    name: nameOverride?.trim() || schedule.name || DEFAULT_AVAILABILITY_NAME,
    timeZone: schedule.timeZone || timeZoneFallback || DEFAULT_TIMEZONE,
    schedule: buildAvailabilityScheduleFromCal({ availability: schedule.availability ?? [] }),
    dateOverrides: buildAvailabilityDateOverridesFromCal({ overrides: schedule.overrides }),
    isDefault,
  };
}

/**
 * Builds normalized form values from a persisted schedule-write payload.
 */
export function buildAvailabilityFormValuesFromScheduleInput({
  scheduleInput,
}: {
  scheduleInput: AvailabilityScheduleInput;
}): AvailabilityFormValues {
  return {
    name: scheduleInput.name?.trim() || DEFAULT_AVAILABILITY_NAME,
    timeZone: scheduleInput.timeZone?.trim() || DEFAULT_TIMEZONE,
    schedule: buildAvailabilityScheduleFromCal({ availability: scheduleInput.availability ?? [] }),
    dateOverrides: buildAvailabilityDateOverridesFromCal({ overrides: scheduleInput.overrides }),
    isDefault: scheduleInput.isDefault,
  };
}

/**
 * Converts a Cal schedule response into the schedule-input payload we persist locally.
 */
export function buildAvailabilityScheduleInputFromCalSchedule({
  schedule,
  isDefault,
  nameOverride,
}: {
  schedule: CalSchedule;
  isDefault: boolean;
  nameOverride?: string;
}): AvailabilityScheduleInput {
  return {
    name: nameOverride?.trim() || schedule.name || DEFAULT_AVAILABILITY_NAME,
    timeZone: schedule.timeZone || DEFAULT_TIMEZONE,
    availability: schedule.availability ?? [],
    isDefault,
    overrides: schedule.overrides ?? [],
  };
}

export function buildAvailabilityScheduleRequest({
  formState,
  timeZoneFallback,
  nameOverride,
  isDefault,
  allowEmptyAvailability = false,
}: {
  formState: AvailabilityFormValues;
  timeZoneFallback?: string;
  nameOverride?: string;
  isDefault: boolean;
  allowEmptyAvailability?: boolean;
}): AvailabilityScheduleInput {
  const availability = toAvailabilityRanges(formState.schedule);
  const overrides = toAvailabilityOverrides(formState.dateOverrides);
  let resolvedAvailability = availability;
  if (availability.length === 0) {
    resolvedAvailability = allowEmptyAvailability ? [] : buildDefaultAvailability();
  }

  return {
    name: nameOverride?.trim() || formState.name?.trim() || DEFAULT_AVAILABILITY_NAME,
    timeZone: formState.timeZone?.trim() || timeZoneFallback || DEFAULT_TIMEZONE,
    availability: resolvedAvailability,
    isDefault,
    // Always send the array. An empty one clears Cal's overrides; `undefined`
    // drops the key from the PATCH and Cal keeps whatever it already has, so
    // removing the last date override would not persist.
    overrides,
  };
}

/**
 * Builds a stable signature for comparing schedules across entities.
 */
export function buildAvailabilityComparisonKey({
  formState,
}: {
  formState?: AvailabilityFormValues | null;
}): string | undefined {
  if (!formState) {
    return undefined;
  }

  const normalizedSchedule = formState.schedule.map((dayRanges) =>
    dayRanges
      .map((range) => {
        const start = formatTimeRangeValue(range.start);
        const end = formatTimeRangeValue(range.end);
        if (!start || !end) {
          return null;
        }

        return {
          start,
          end,
        };
      })
      .filter((range): range is { start: string; end: string } => range !== null),
  );
  const normalizedDateOverrides = buildAvailabilityDateOverridesFromCal({
    overrides: toAvailabilityOverrides(formState.dateOverrides),
  });

  return JSON.stringify({
    timeZone: formState.timeZone.trim() || DEFAULT_TIMEZONE,
    schedule: normalizedSchedule,
    dateOverrides: normalizedDateOverrides,
    inheritHostAvailability: formState.inheritHostAvailability || undefined,
  });
}

/**
 * Returns true when two availability schedules differ.
 */
export function doAvailabilitySchedulesDiffer({
  left,
  right,
}: {
  left?: AvailabilityFormValues | null;
  right?: AvailabilityFormValues | null;
}): boolean {
  const leftKey = buildAvailabilityComparisonKey({ formState: left });
  const rightKey = buildAvailabilityComparisonKey({ formState: right });
  if (!leftKey || !rightKey) {
    return false;
  }

  return leftKey !== rightKey;
}
